import { useEffect, useState } from 'react';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../dialogs/CortexDialog';
import { Button, Chip, CoordioDropdown, Textarea, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { Flag as OutcomeIcon } from 'lucide-react';
import { memoryModuleService } from '../../services/memoryModuleService';
import { useNotificationStore } from '../../store/notificationStore';
import {
  OUTCOME_SOURCES,
  SESSION_OUTCOMES,
  type SessionMemoryOutcomeDto,
} from '../../types/memoryModule';

interface SessionMemoryOutcomeProps {
  sessionId: string;
}

const outcomeColorMap: Record<string, 'green' | 'red' | 'amber' | 'default'> = {
  Completed: 'green',
  Incorrect: 'red',
  Incomplete: 'amber',
  Abandoned: 'amber',
};

const outcomeColor = (outcome: string | null | undefined): 'green' | 'red' | 'amber' | 'default' => {
  return outcomeColorMap[outcome ?? ''] ?? 'default';
};

/**
 * Late-binding memory outcome for one session (Memory spec 02 §6.4). Renders nothing when
 * the session was never queued. The dialog never shows transcript or identifiers.
 */
export const SessionMemoryOutcome = ({ sessionId }: SessionMemoryOutcomeProps) => {
  const { showNotification } = useNotificationStore();
  const [state, setState] = useState<SessionMemoryOutcomeDto | null>(null);
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState('Completed');
  const [source, setSource] = useState('UserConfirmed');
  const [failureReason, setFailureReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    void memoryModuleService
      .getSessionOutcome(sessionId)
      .then(setState)
      .catch(() => setState(null));
  };

  useEffect(() => {
    load();
  }, [sessionId]);

  if (!state?.found) {
    return null;
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      const receipt = await memoryModuleService.assignSessionOutcome(sessionId, {
        outcome,
        outcomeSource: source,
        failureReason: outcome === 'Incorrect' ? failureReason.trim() || undefined : undefined,
      });
      const parts = [`Outcome set to ${receipt.outcome} (rev ${receipt.outcomeRevisionCount})`];
      if (receipt.forcedExtraction) parts.push('extraction reopened');
      showNotification(parts.join(' — '), 'success');
      setOpen(false);
      load();
    } catch (err: any) {
      showNotification(err.response?.data?.message || err.message || 'Failed to set outcome', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className="flex flex-wrap items-center gap-3 border-b border-border px-6 py-2.5"
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5">
                <OutcomeIcon size={18} className="text-muted-foreground" />
                <span className="text-xs font-semibold text-muted-foreground">
                  Memory outcome
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent>The episodic label is late-binding — set or revise it here. Revisions append; the prior label is kept.</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Chip
          color={outcomeColor(state.outcome)}
          label={state.outcome ?? 'Unknown'}
          variant="filled"
        />
        {state.outcomeSource && (
          <Chip label={state.outcomeSource} variant="outlined" color="default" />
        )}
        {state.outcomeRevisionCount > 0 && (
          <span className="text-xs text-muted-foreground">
            rev {state.outcomeRevisionCount}
          </span>
        )}
        {state.isDeferred && <Chip label="Deferred" color="amber" variant="outlined" />}
        {state.hasStub && <Chip label="Stub" variant="outlined" color="default" />}
        {state.hasEpisode && <Chip label="Episode" variant="outlined" color="default" />}
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setOutcome(state.outcome && state.outcome !== 'Unknown' ? state.outcome : 'Completed');
            setSource(state.outcomeSource && state.outcomeSource !== 'SelfAssessed'
              ? state.outcomeSource
              : 'UserConfirmed');
            setFailureReason(state.failureReason ?? '');
            setOpen(true);
          }}
        >
          Set outcome
        </Button>
      </div>

      <CortexDialog open={open} onClose={() => setOpen(false)} title="Set memory outcome" size="lg" data-testid="memory-outcome-dialog">
        <CortexDialogBody>
          <div className="flex flex-col gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">Outcome</label>
              <CoordioDropdown
                options={SESSION_OUTCOMES.map((o) => ({ value: o, label: o }))}
                value={outcome}
                onChange={(val) => setOutcome(val ?? '')}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">Source</label>
              <CoordioDropdown
                options={OUTCOME_SOURCES.map((s) => ({ value: s, label: s }))}
                value={source}
                onChange={(val) => setSource(val ?? '')}
              />
            </div>
            {outcome === 'Incorrect' && (
              <div>
                <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">Failure reason</label>
                <Textarea
                  className="w-full"
                  value={failureReason}
                  onChange={(e) => setFailureReason(e.target.value)}
                  rows={2}
                />
              </div>
            )}
          </div>
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </CortexDialogFooter>
      </CortexDialog>
    </>
  );
};
